import { z } from "zod";

import { API_BASE } from "@/config/api";
import { fetchWithAuth } from "@/lib/api";
import { parseApiResponse } from "@/lib/api-validation";
import { assetSchema } from "@/schemas/asset.schema";
import {
  budgetOverviewSchema,
  budgetReviewItemSchema,
  budgetRuleSchema,
} from "@/schemas/budget.schema";

import type { Asset, BudgetOverview, BudgetReviewItem, BudgetReviewResolution, BudgetRule } from "@/schemas";

const budgetRulesResponseSchema = z.object({
  rules: z.array(budgetRuleSchema),
});

const budgetReviewsResponseSchema = z.object({
  reviews: z.array(budgetReviewItemSchema),
});

const assetsResponseSchema = z.object({
  results: z.array(assetSchema),
});

export async function getBudgetOverview(month: string): Promise<BudgetOverview | null> {
  try {
    const response = await fetchWithAuth(`${API_BASE}/budget/overview?month=${month}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch budget overview: ${response.statusText}`);
    }
    return parseApiResponse(budgetOverviewSchema, await response.json(), "getBudgetOverview");
  } catch (error) {
    console.error("Error fetching budget overview:", error);
    return null;
  }
}

export async function getBudgetRules(): Promise<BudgetRule[]> {
  try {
    const response = await fetchWithAuth(`${API_BASE}/budget/rules`);
    if (!response.ok) {
      throw new Error(`Failed to fetch budget rules: ${response.statusText}`);
    }
    const data = parseApiResponse(budgetRulesResponseSchema, await response.json(), "getBudgetRules");
    return data.rules;
  } catch (error) {
    console.error("Error fetching budget rules:", error);
    return [];
  }
}

export async function createBudgetRule(rule: Partial<BudgetRule>): Promise<BudgetRule> {
  const response = await fetchWithAuth(`${API_BASE}/budget/rules`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(rule),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || "Failed to create budget rule");
  }
  const data = await response.json();
  return parseApiResponse(z.object({ rule: budgetRuleSchema }), data, "createBudgetRule").rule;
}

export async function updateBudgetRule(ruleId: string, rule: Partial<BudgetRule>): Promise<BudgetRule> {
  const response = await fetchWithAuth(`${API_BASE}/budget/rules/${ruleId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(rule),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || "Failed to update budget rule");
  }
  const data = await response.json();
  return parseApiResponse(z.object({ rule: budgetRuleSchema }), data, "updateBudgetRule").rule;
}

export async function deleteBudgetRule(ruleId: string): Promise<void> {
  const response = await fetchWithAuth(`${API_BASE}/budget/rules/${ruleId}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || "Failed to delete budget rule");
  }
}

export async function runBudgetRule(ruleId: string): Promise<void> {
  const response = await fetchWithAuth(`${API_BASE}/budget/rules/${ruleId}/run`, {
    method: "POST",
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || "Failed to execute budget rule");
  }
}

export async function getBudgetReviews(month: string): Promise<BudgetReviewItem[]> {
  try {
    const response = await fetchWithAuth(`${API_BASE}/budget/reviews?month=${month}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch budget reviews: ${response.statusText}`);
    }
    const data = parseApiResponse(budgetReviewsResponseSchema, await response.json(), "getBudgetReviews");
    return data.reviews;
  } catch (error) {
    console.error("Error fetching budget reviews:", error);
    return [];
  }
}

export async function resolveBudgetReview(reviewId: string, resolution: BudgetReviewResolution): Promise<void> {
  const response = await fetchWithAuth(`${API_BASE}/budget/reviews/${reviewId}/resolve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ resolution }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || "Failed to resolve budget review");
  }
}

export async function getFiatAssets(): Promise<Asset[]> {
  try {
    const response = await fetchWithAuth(`${API_BASE}/assets?type=fiat`);
    if (!response.ok) {
      throw new Error(`Failed to fetch fiat assets: ${response.statusText}`);
    }
    const data = parseApiResponse(assetsResponseSchema, await response.json(), "getFiatAssets");
    return data.results;
  } catch (error) {
    console.error("Error fetching fiat assets:", error);
    return [];
  }
}
